import React, { useEffect, useState } from "react";
import { api, rupiah } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Search, Wallet, PiggyBank, BookOpen, ArrowDownCircle, ArrowUpCircle, Loader2, FileText, FileSpreadsheet, Printer } from "lucide-react";
import { printReceipt } from "@/components/ReceiptDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-");

export default function AdminSaldoSantri() {
  const [students, setStudents] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/admin/students").then((r) => setStudents(r.data)).catch(() => {});
  }, []);

  const pick = (s) => {
    setSelected(s);
    setLoading(true);
    api.get(`/admin/students/${s.id}/ledger`).then((r) => setLedger(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  const dl = async (format) => {
    if (!selected) return;
    try {
      const r = await api.get(`/admin/students/${selected.id}/ledger-export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `buku_tabungan_${selected.nisn || selected.id}.${format === "xlsx" ? "xlsx" : "pdf"}`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const filtered = query
    ? students.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()) || (s.nisn || "").includes(query)).slice(0, 8)
    : [];
  const rows = (ledger?.rows || []).filter((r) => cat === "all" || r.category === cat);

  return (
    <div>
      <PageHeader title="History Uang Jajan Santri" subtitle="Buku tabungan tiap santri: uang jajan & tabungan masuk/keluar, tanggal-jam, dan sumber dana (Bank, Admin, atau Kasir)."
        action={selected && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => dl("pdf")} data-testid="admin-ledger-pdf" className="rounded-xl"><FileText size={16} className="mr-1.5" /> PDF</Button>
            <Button variant="outline" onClick={() => dl("xlsx")} data-testid="admin-ledger-xlsx" className="rounded-xl"><FileSpreadsheet size={16} className="mr-1.5" /> Excel</Button>
          </div>
        )}
      />

      <div className="relative max-w-md mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="saldo-search" placeholder="Cari santri (nama / NISN)…" className="pl-10 rounded-xl bg-white h-12" />
        {filtered.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-stone-200 rounded-xl shadow-lg overflow-hidden">
            {filtered.map((s) => (
              <button key={s.id} data-testid={`saldo-pick-${s.id}`} onClick={() => { pick(s); setQuery(""); }} className="w-full text-left px-4 py-2.5 hover:bg-emerald-50 text-sm">
                <span className="font-medium">{s.name}</span> <span className="text-stone-400 text-xs">· {s.nisn} · {s.gender}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {!selected ? (
        <Empty><BookOpen className="mx-auto mb-2" /> Cari dan pilih santri untuk melihat history saldonya.</Empty>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mb-5">
            <div className="bg-white border border-stone-200 rounded-2xl p-4 shadow-sm">
              <p className="text-sm text-stone-500">Santri</p>
              <p className="font-heading font-bold text-stone-900 mt-1" data-testid="saldo-student-name">{ledger?.student?.name || selected.name}</p>
              <p className="text-xs text-stone-400 font-mono">NISN {ledger?.student?.nisn || selected.nisn}</p>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl p-4 shadow-sm">
              <p className="text-[11px] uppercase tracking-wider text-stone-400 flex items-center gap-1"><Wallet size={12} /> Uang Jajan</p>
              <p className="font-heading font-bold text-stone-900 mt-1">{rupiah(ledger?.student?.personal_balance || 0)}</p>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl p-4 shadow-sm">
              <p className="text-[11px] uppercase tracking-wider text-stone-400 flex items-center gap-1"><PiggyBank size={12} /> Tabungan</p>
              <p className="font-heading font-bold text-stone-900 mt-1">{rupiah(ledger?.student?.savings_balance || 0)}</p>
            </div>
          </div>

          <Tabs value={cat} onValueChange={setCat} className="mb-4">
            <TabsList>
              <TabsTrigger value="all">Semua</TabsTrigger>
              <TabsTrigger value="saldo">Uang Jajan</TabsTrigger>
              <TabsTrigger value="tabungan">Tabungan</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
            ) : rows.length === 0 ? (
              <Empty><BookOpen className="mx-auto mb-2" /> Belum ada mutasi uang jajan.</Empty>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead>Tanggal & Jam</TableHead>
                    <TableHead>Keterangan</TableHead>
                    <TableHead>Jenis</TableHead>
                    <TableHead className="text-right">Masuk</TableHead>
                    <TableHead className="text-right">Keluar</TableHead>
                    <TableHead>Petugas</TableHead>
                    <TableHead className="text-right">Kuitansi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={i} data-testid={`admin-ledger-row-${i}`}>
                      <TableCell className="text-sm whitespace-nowrap">{fmt(r.date)}</TableCell>
                      <TableCell className="text-sm">{r.note}</TableCell>
                      <TableCell>
                        <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${r.category === "tabungan" ? "bg-violet-50 text-violet-700" : "bg-sky-50 text-sky-700"}`}>
                          {r.category === "tabungan" ? "Tabungan" : "Uang Jajan"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-emerald-600">
                        {r.direction === "in" ? <span className="inline-flex items-center gap-1 justify-end"><ArrowDownCircle size={13} /> {rupiah(r.amount)}</span> : "-"}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-rose-600">
                        {r.direction === "out" ? <span className="inline-flex items-center gap-1 justify-end"><ArrowUpCircle size={13} /> {rupiah(r.amount)}</span> : "-"}
                      </TableCell>
                      <TableCell className="text-sm text-stone-500">{r.source}</TableCell>
                      <TableCell className="text-right">
                        {r.receipt && r.id && (
                          <Button size="sm" variant="outline" onClick={() => printReceipt(r.id)} data-testid={`print-receipt-${r.id}`} className="rounded-lg h-8">
                            <Printer size={13} className="mr-1" /> Cetak
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
